import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GuildDetailResponse, UpdateGuildPayload } from '@/lib/api';

interface UseGuildReturn {
  guild: GuildDetailResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  update: (input: UpdateGuildPayload) => Promise<void>;
  remove: () => Promise<void>;
  join: () => Promise<void>;
  leave: () => Promise<void>;
}

export function useGuild(guildId: string | undefined): UseGuildReturn {
  const [guild, setGuild] = useState<GuildDetailResponse | null>(null);
  const [loading, setLoading] = useState(!!guildId);
  const [error, setError] = useState<string | null>(null);
  const refreshRef = useRef(0);

  const fetchGuild = useCallback(async () => {
    if (!guildId) {
      setLoading(false);
      return;
    }
    const id = ++refreshRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await api.getGuildDetail(guildId);
      if (refreshRef.current === id) {
        setGuild(result);
      }
    } catch (err) {
      if (refreshRef.current === id) {
        setError(err instanceof Error ? err.message : 'Failed to load guild');
      }
    } finally {
      if (refreshRef.current === id) {
        setLoading(false);
      }
    }
  }, [guildId]);

  const update = useCallback(
    async (input: UpdateGuildPayload) => {
      if (!guildId) return;
      const result = await api.updateGuild(guildId, input);
      setGuild((prev) => ({ ...prev, ...result, myRole: result.myRole ?? prev?.myRole ?? null }));
    },
    [guildId],
  );

  const remove = useCallback(async () => {
    if (!guildId) return;
    await api.deleteGuild(guildId);
  }, [guildId]);

  const join = useCallback(async () => {
    if (!guildId) return;
    await api.joinGuild(guildId);
    await fetchGuild();
  }, [guildId, fetchGuild]);

  const leave = useCallback(async () => {
    if (!guildId) return;
    await api.leaveGuild(guildId);
    await fetchGuild();
  }, [guildId, fetchGuild]);

  useEffect(() => {
    fetchGuild();
    return () => {
      refreshRef.current++;
    };
  }, [fetchGuild]);

  return {
    guild,
    loading,
    error,
    refresh: fetchGuild,
    update,
    remove,
    join,
    leave,
  };
}
